/**
 * Converting a picked local date/time into the instant the API stores.
 *
 * Used by the SuperAdmin API Tokens page
 * (`pages/superadmin/ApiTokens.vue`) for an internal API token's optional
 * expiry. Jake, 2026-09-26: the free-text ISO-8601 field the card shipped
 * with (#3810) becomes a real date/time control that shows LOCAL time and
 * sends UTC.
 *
 * THE BACKEND CONTRACT, AND WHY THE SUFFIX IS LOAD-BEARING
 * ========================================================
 * `api/src/endpoints/internal_api_tokens.py::_parse_expires_at_or_abort`
 * parses with `datetime.fromisoformat` and then:
 *
 *     if parsed.tzinfo is None:
 *         parsed = parsed.replace(tzinfo=timezone.utc)
 *
 * A NAIVE STRING IS SILENTLY ASSUMED TO BE UTC. The service then normalizes
 * with `_naive_utc` (`services/internal_api_tokens.py:263`) and stores into a
 * TIMESTAMP WITHOUT TIME ZONE column.
 *
 * A `datetime-local` input's value is a naive LOCAL string -- `2027-03-15T09:30`
 * -- so posting it unchanged would store 09:30 UTC: seven hours early in US
 * Pacific daylight, five and a half hours late in India. The operator sees the
 * number they typed in the token list and has no way to tell it is wrong. The
 * partner's integration then dies at a time nobody predicted.
 *
 * So every value that leaves here carries an EXPLICIT instant -- `toISOString()`,
 * which ends in `Z` -- and the assume-UTC branch above never runs. `Z` is
 * accepted by `datetime.fromisoformat` on the deployed runtime (python:3.13,
 * per `api/Dockerfile`; `Z` support landed in 3.11).
 *
 * WHY THE OFFSET IS AN ARGUMENT RATHER THAN READ FROM `Date`
 * =========================================================
 * So the conversion is testable against real non-zero offsets on any machine.
 * A test relying on the runner's own zone asserts nothing on a machine already
 * in UTC -- it would pass while exercising only the case where a missing
 * conversion is invisible. Callers pass `new Date().getTimezoneOffset()`.
 */

/**
 * Minutes to add to local time to reach UTC, as `Date#getTimezoneOffset`
 * reports it. NOTE THE SIGN IS INVERTED relative to an ISO offset: UTC-07:00
 * is +420 and UTC+05:30 is -330. Reversing it lands the result twice the
 * offset away from the truth, in the direction that looks plausible.
 *
 * @typedef {number} TimezoneOffsetMinutes
 */

/** Matches what a `datetime-local` input produces, with optional seconds. */
const LOCAL_INPUT_RE =
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;

function pad2(n) {
    return String(n).padStart(2, "0");
}

/**
 * Convert a `datetime-local` value into an explicit UTC instant string.
 *
 * Returns `null` -- never a guessed instant -- for anything it cannot read.
 * An empty field legitimately means "no expiry" (`expires_at` is nullable;
 * NULL means no expiry per `models/internal_api_token.py`), and a fabricated
 * date would be precisely the silently-wrong expiry this module exists to
 * prevent.
 *
 * @param {string|null|undefined} localValue e.g. `"2027-03-15T09:30"`.
 * @param {TimezoneOffsetMinutes} offsetMinutes
 * @returns {string|null} e.g. `"2027-03-15T16:30:00.000Z"`.
 */
export function localToUtcInstant(localValue, offsetMinutes) {
    const raw = String(localValue ?? "").trim();
    if (!raw) return null;

    const m = LOCAL_INPUT_RE.exec(raw);
    if (!m) return null;

    const [, y, mo, d, h, mi, s] = m;
    // `Date.UTC` treats the parts as if they were UTC, which is exactly the
    // intermediate we want: adding the offset then yields the real instant.
    // Going through `new Date(localString)` instead would read the machine's
    // own zone and ignore the offset argument, which is the bug that makes
    // this function untestable.
    const asIfUtc = Date.UTC(
        Number(y),
        Number(mo) - 1,
        Number(d),
        Number(h),
        Number(mi),
        s === undefined ? 0 : Number(s)
    );
    if (Number.isNaN(asIfUtc)) return null;

    // Reject a value whose parts do not survive the round trip -- `2027-13-45`
    // matches the shape but `Date.UTC` rolls it into a real date 13 months
    // later, and accepting that would store an expiry the operator never
    // picked. The regex cannot catch it; only the round trip can.
    const probe = new Date(asIfUtc);
    if (
        probe.getUTCFullYear() !== Number(y) ||
        probe.getUTCMonth() !== Number(mo) - 1 ||
        probe.getUTCDate() !== Number(d) ||
        probe.getUTCHours() !== Number(h) ||
        probe.getUTCMinutes() !== Number(mi)
    ) {
        return null;
    }

    const offset = Number(offsetMinutes);
    if (!Number.isFinite(offset)) return null;

    return new Date(asIfUtc + offset * 60000).toISOString();
}

/**
 * The timezone offset in force AT the picked local instant.
 *
 * WHY THIS EXISTS AND WHY `new Date().getTimezoneOffset()` IS WRONG
 * ================================================================
 * The offset a caller most obviously reaches for is today's. That is correct
 * only while the picked date is on the SAME side of a DST boundary as today --
 * and for a token expiry it usually is not, because expiries are set months
 * out.
 *
 * Measured on US Pacific before this function existed: picking
 * `2027-01-15T09:30` while today was in July produced `16:30Z`, where the true
 * instant is `17:30Z`. One hour early, silently, on the one surface whose whole
 * purpose is to stop an expiry landing at a time nobody chose. Found in
 * self-review rather than by a test, which is why the DST cases are now pinned
 * in `__tests__/tokenExpiry.spec.js`.
 *
 * The parse here is `new Date(localValue)`, which for a date-TIME form with no
 * offset is LOCAL per ECMA-262 -- exactly the interpretation we want, since we
 * are asking "what offset applies to this wall-clock moment". (A date-ONLY
 * form would be parsed as UTC, which is the classic trap; this function is only
 * ever handed the picker's date-time value.)
 *
 * Falls back to today's offset for an unparseable value, keeping the function
 * total: `localToUtcInstant` returns null for that same input, so the caller
 * discards the result regardless, and a second null to thread through would be
 * cost with no benefit.
 *
 * @param {string|null|undefined} localValue e.g. `"2027-01-15T09:30"`.
 * @returns {TimezoneOffsetMinutes}
 */
export function offsetForLocalValue(localValue) {
    const raw = String(localValue ?? "").trim();
    if (raw) {
        const at = new Date(raw);
        if (!Number.isNaN(at.getTime())) {
            return at.getTimezoneOffset();
        }
    }
    return new Date().getTimezoneOffset();
}

/**
 * Render a UTC instant so an operator can read what will be stored.
 *
 * The word "UTC" is part of the RETURNED STRING rather than adjacent prose:
 * an unlabelled timestamp beside a local-time picker is the exact ambiguity
 * this module removes, and a layout change can separate a label from a number
 * while a single string cannot be pulled apart.
 *
 * Returns `""` -- not `"Invalid Date"` -- for junk. There is no instant to
 * report, and "Invalid Date" beside a mint button reads as a system fault.
 *
 * @param {string|null|undefined} isoInstant
 * @returns {string} e.g. `"2027-03-15 16:30 UTC"`.
 */
export function formatUtcForDisplay(isoInstant) {
    const raw = String(isoInstant ?? "").trim();
    if (!raw) return "";

    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return "";

    return (
        `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-` +
        `${pad2(d.getUTCDate())} ${pad2(d.getUTCHours())}:` +
        `${pad2(d.getUTCMinutes())} UTC`
    );
}

/**
 * Format an instant into the value a `datetime-local` input accepts.
 *
 * The shape is exactly `YYYY-MM-DDTHH:mm`; anything else the browser rejects
 * SILENTLY, leaving the field empty -- which looks like the operator never
 * picked anything rather than like a formatting bug. Hence the zero padding
 * and the invalid-date guard.
 *
 * @param {Date|null|undefined} date
 * @param {TimezoneOffsetMinutes} offsetMinutes
 * @returns {string}
 */
export function localInputFromDate(date, offsetMinutes) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";

    const offset = Number(offsetMinutes);
    if (!Number.isFinite(offset)) return "";

    // Subtract the offset to go from the instant back to local wall time,
    // then read the parts in UTC -- the mirror of localToUtcInstant, and the
    // reason the round trip in the spec is exact rather than approximate.
    const local = new Date(date.getTime() - offset * 60000);
    return (
        `${local.getUTCFullYear()}-${pad2(local.getUTCMonth() + 1)}-` +
        `${pad2(local.getUTCDate())}T${pad2(local.getUTCHours())}:` +
        `${pad2(local.getUTCMinutes())}`
    );
}
